<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, ref, watch } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);

const emptyRuntime = {
  actions: { finance: {} },
  data: ref({}),
  error: ref(''),
  loading: ref(false),
  refresh: async () => null,
  saving: ref(false),
  selectedId: ref(''),
};
const realData = inject('imudeiRealData', null) || emptyRuntime;
const model = useImudeiRuntimeModel({
  realData,
  module: computed(() => 'finance'),
  screen: computed(() => 'edit'),
});

const actionReason = ref('');
const notice = ref('');
const selectedResponsibilityId = ref('');

const valueAt = (value, path) =>
  path.split('.').reduce((current, key) => current?.[key], value);
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => valueAt(value, path))
    .find(item => item !== undefined && item !== null && item !== '');
  return result ?? fallback;
};
const asArray = value => (Array.isArray(value) ? value : []);
const text = value => String(value || '').replaceAll('_', ' ');
const titleCase = value =>
  text(value).replace(/\b\w/g, letter => letter.toLocaleUpperCase('pt-BR'));
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const displayText = value => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  if (typeof value === 'object') {
    return firstValue(value, ['label', 'name', 'formatted', 'display'], '—');
  }
  return String(value);
};
const formatMoney = value => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  if (typeof value === 'object') {
    const formatted = firstValue(value, ['formatted', 'display', 'label']);
    if (formatted) return formatted;
    return formatMoney(value.amount ?? value.value ?? value.amount_cents);
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return new Intl.NumberFormat('pt-BR', {
    currency: 'BRL',
    style: 'currency',
  }).format(amount);
};
const formatDate = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(parsed);
};
const formatDateTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};
const formatPoints = value => {
  if (value === undefined || value === null || value === '') return '';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return `${new Intl.NumberFormat('pt-BR').format(amount)} pontos`;
};
const statusLabel = value =>
  displayText(typeof value === 'object' ? value?.label || value?.slug : value);
const eventLabel = event => {
  const targetStatus = firstValue(event, ['status_novo', 'to_status']);
  const type = firstValue(event, ['tipo', 'event_type', 'action']);
  if (targetStatus) return `Status atualizado para ${titleCase(targetStatus)}`;
  if (type) return titleCase(type);
  return displayText(firstValue(event, ['motivo', 'reason', 'notes']));
};

const source = computed(() => model.detail.value?.source || realData.data.value?.detail || {});
const installment = computed(() => source.value.parcela || source.value);
const process = computed(() => source.value.dados_processo || source.value.processo || {});
const financeActions = computed(() => source.value.acoes || source.value.actions || {});
const error = computed(() => realData.error?.value || '');
const loading = computed(() => Boolean(realData.loading?.value));
const saving = computed(() => Boolean(realData.saving?.value));
const gamification = computed(() =>
  firstValue(source.value, ['gamificacao', 'gamification', 'pontuacao', 'score'], {})
);
const gamificationOwner = computed(() =>
  firstValue(gamification.value, [
    'usuario.name',
    'user.name',
    'owner.name',
    'agent.name',
  ]) || firstValue(process.value, ['responsible.name', 'manager.name'])
);
const gamificationLevel = computed(() =>
  firstValue(gamification.value, [
    'nivel.label',
    'level.label',
    'nivel.name',
    'level.name',
    'nivel',
    'level',
    'rank',
  ])
);
const gamificationLevelLabel = computed(() => {
  if (!gamificationLevel.value) return 'Nível indisponível';
  const level = displayText(gamificationLevel.value);
  return /nível/i.test(level) ? level : `Nível ${level}`;
});
const gamificationStreak = computed(() =>
  firstValue(gamification.value, [
    'sequencia.label',
    'streak.label',
    'sequencia',
    'streak',
  ])
);
const gamificationPoints = computed(() =>
  formatPoints(
    firstValue(gamification.value, [
      'pontos',
      'points',
      'total_pontos',
      'total_points',
      'balance',
    ])
  )
);
const gamificationProgress = computed(() => {
  const value = Number(
    firstValue(gamification.value, [
      'progresso',
      'progress',
      'percentual',
      'percentage',
    ])
  );
  return Number.isFinite(value) ? Math.min(Math.max(value, 0), 100) : null;
});
const gamificationProgressClass = computed(() => {
  const progress = gamificationProgress.value;
  if (progress === null || progress === 0) return 'w-0';
  if (progress < 17) return 'w-1/6';
  if (progress < 34) return 'w-1/3';
  if (progress < 51) return 'w-1/2';
  if (progress < 67) return 'w-2/3';
  if (progress < 84) return 'w-5/6';
  return 'w-full';
});
const pointRuleIcons = [
  'i-lucide-file-check-2',
  'i-lucide-refresh-cw',
  'i-lucide-chart-no-axes-combined',
  'i-lucide-scan-line',
];
const fallbackPointRules = [
  { icon: pointRuleIcons[0], id: 'valid-launch', label: 'Lançamento válido', points: '' },
  { icon: pointRuleIcons[1], id: 'reconcile', label: 'Conciliar', points: '' },
  { icon: pointRuleIcons[2], id: 'receipt', label: 'Comprovante aprovado', points: '' },
  { icon: pointRuleIcons[3], id: 'close', label: 'Fechamento em dia', points: '' },
];
const pointRules = computed(() => {
  const rules = asArray(
    firstValue(gamification.value, [
      'regras',
      'rules',
      'regras_pontos',
      'point_rules',
    ])
  )
    .map((rule, index) => {
      const label = firstValue(rule, ['label', 'name', 'titulo', 'title']);
      if (!label) return null;
      return {
        icon: pointRuleIcons[index % pointRuleIcons.length],
        id: String(rule.id || `${label}-${index}`),
        label,
        points: formatPoints(
          firstValue(rule, ['pontos', 'points', 'valor', 'value', 'amount'])
        ),
      };
    })
    .filter(Boolean)
    .slice(0, 4);

  return rules.length ? rules : fallbackPointRules;
});
const recordId = computed(() => model.detail.value?.id || installment.value.id || source.value.id || '');
const recordCode = computed(() =>
  firstValue(process.value, ['codigo', 'reference'], recordId.value ? `#${recordId.value}` : '—')
);
const recordTitle = computed(() =>
  firstValue(process.value, ['titulo', 'title'], model.detail.value?.title || 'Lançamento financeiro')
);
const recordAddress = computed(() => {
  const address = installment.value.endereco || source.value.endereco || {};
  return [address.logradouro, address.numero, address.unidade, address.bairro]
    .filter(Boolean)
    .join(' · ') || '—';
});
const recordManager = computed(() =>
  firstValue(process.value, ['responsible.name', 'manager.name'])
);
const recordContext = computed(() =>
  [recordAddress.value, recordManager.value].filter(Boolean).join(' · ') || '—'
);
const currentStatus = computed(() => installment.value.status || source.value.status || {});
const suggestedStatus = computed(() =>
  firstValue(source.value, ['sugestao_status', 'suggested_status'])
);
const canTransition = computed(() =>
  Boolean(
    financeActions.value.pode_alterar_status ||
      financeActions.value.can_transition ||
      source.value.permissions?.can_transition
  )
);
const movementType = computed(() =>
  text(
    firstValue(source.value, [
      'tipo_lancamento',
      'movement_type',
      'entry_type',
      'kind',
    ])
  ).toLocaleLowerCase('pt-BR')
);
const isReceipt = computed(() => /receb|entrada|income/.test(movementType.value));
const isPayment = computed(() => /pag|saida|expense/.test(movementType.value));
const payers = computed(() => {
  const entries = [
    ...asArray(source.value.compradores_vendedores),
    ...asArray(source.value.usuarios_agrupado),
    ...asArray(source.value.usuarios_agrupados),
    ...asArray(installment.value.responsaveis_pagamento),
    ...asArray(installment.value.usuarios_agrupados),
  ];
  return entries
    .map((entry, index) => {
      const name = firstValue(entry, [
        'usuario_nome',
        'usuario.name',
        'usuario.nome',
        'empresa.name',
        'empresa.nome',
        'name',
        'label',
        'contact.name',
      ]);
      if (!name) return null;
      return {
        id: String(entry.id || entry.pagamento_id || `${name}-${index}`),
        name,
        role: firstValue(entry, ['papel', 'role', 'tipo_label', 'tipo']),
      };
    })
    .filter(Boolean)
    .filter(
      (participant, index, list) =>
        list.findIndex(item => item.name === participant.name) === index
    )
    .slice(0, 3);
});
const paymentResponsibilities = computed(() =>
  asArray(installment.value.responsaveis_pagamento).map(responsibility => ({
    confirmed: Boolean(
      responsibility.confirmacao?.confirmada ||
        responsibility.confirmacao_pagamento ||
        responsibility.comfirmacao_pagamento
    ),
    id: String(responsibility.id || responsibility.pagamento_id || ''),
    name: firstValue(responsibility, ['usuario_nome', 'responsavel_nome'], '—'),
    canConfirm: Boolean(
      responsibility.acoes?.pode_confirmar ||
        responsibility.actions?.can_confirm ||
        responsibility.can_confirm
    ),
    value: formatMoney(responsibility.valor_pagamento || responsibility.pagamento?.valor),
  }))
);
const pendingResponsibilities = computed(() =>
  paymentResponsibilities.value.filter(item => item.id && !item.confirmed && item.canConfirm)
);
const rateio = computed(() => source.value.rateio || installment.value.rateio || {});
const commission = computed(() => source.value.comissao_geral || {});
const commissionInstallment = computed(() => {
  const installments = asArray(commission.value.parcelas);
  return (
    installments.find(item => String(item.id) === String(recordId.value)) ||
    installments.find(
      item => String(item.numero) === String(installment.value.numero_parcela)
    ) ||
    installments[0] ||
    {}
  );
});
const signatureDate = computed(() =>
  firstValue(installment.value, [
    'data_assinatura',
    'signed_at',
  ]) || firstValue(process.value, ['data_assinatura', 'signed_at'])
);
const createdStatusAt = computed(() =>
  firstValue(installment.value, ['data_criacao_status', 'created_at'])
);
const dueDate = computed(() =>
  firstValue(installment.value, [
    'data_prevista',
    'due_at',
    'due_date',
  ]) || firstValue(commissionInstallment.value, ['previsao', 'due_at'])
);
const competenceDate = computed(() =>
  firstValue(source.value, ['data_competencia', 'competence_on'])
);
const rateioAvailable = computed(() =>
  formatMoney(
    firstValue(rateio.value, [
      'valor_restante',
      'remaining_amount',
      'available_amount',
    ])
  )
);
const rateioTransferred = computed(() =>
  formatMoney(
    firstValue(rateio.value, [
      'valor_transferido',
      'transferred_amount',
      'paid_amount',
    ])
  )
);
const grossCommission = computed(() =>
  formatMoney(
    firstValue(commission.value, [
      'valor_comissao_total',
      'total_amount',
    ])
  )
);
const netCommission = computed(() =>
  formatMoney(
    firstValue(commission.value, [
      'valor_comissao_liquida',
      'net_amount',
    ])
  )
);
const actionAvailability = computed(() => {
  const labels = [
    [financeActions.value.pode_alterar_status, 'alterar status'],
    [financeActions.value.pode_cancelar, 'cancelar lançamento'],
    [financeActions.value.pode_editar_pagadores, 'editar pagadores'],
    [financeActions.value.pode_liberar_rateio, 'liberar rateio'],
  ]
    .filter(([allowed]) => allowed)
    .map(([, label]) => label);

  return labels.length
    ? `Permissões da API: ${labels.join(', ')}.`
    : 'A API não retornou permissões operacionais para este lançamento.';
});
const events = computed(() => {
  const detailEvents = asArray(
    firstValue(source.value, ['historico', 'history', 'events'], [])
  );
  return detailEvents
    .map((event, index) => ({
      actor: firstValue(event, ['ator.name', 'actor.name', 'user.name', 'author.name'], '—'),
      date: formatDateTime(
        firstValue(event, ['ocorrido_em', 'occurred_at', 'created_at', 'updated_at'])
      ),
      id: String(event.id || event.sequencia || `${eventLabel(event)}-${index}`),
      label: eventLabel(event),
      reason: firstValue(event, ['motivo', 'reason', 'notes']),
    }))
    .slice(0, 5);
});
const transferItems = computed(() =>
  asArray(source.value.transferencias)
    .map((transfer, index) => {
      const beneficiary = firstValue(transfer, [
        'beneficiario.name',
        'beneficiary.name',
        'usuario.name',
        'empresa.name',
        'name',
      ]);
      const status = firstValue(transfer, ['status.label', 'status', 'state']);
      const value = formatMoney(
        firstValue(transfer, ['valor', 'amount', 'value', 'valor_transferido'])
      );

      return {
        actor: beneficiary || 'Rateio',
        date: formatDateTime(
          firstValue(transfer, [
            'data_transferencia',
            'transferred_at',
            'created_at',
            'updated_at',
          ])
        ),
        id: String(transfer.id || `${beneficiary || status}-${index}`),
        label: status
          ? `Transferência ${titleCase(status)}`
          : 'Transferência de rateio',
        value: value === '—' ? '' : value,
      };
    })
    .slice(0, 3)
);
const auditItems = computed(() => events.value.slice(0, 2));
const activityItems = computed(() =>
  [...events.value.slice(2, 5), ...transferItems.value].slice(0, 3)
);
const fields = computed(() => [
  {
    icon: 'i-lucide-circle-dot',
    label: 'Categoria',
    selectLike: true,
    value: titleCase(
      displayText(
        firstValue(source.value, [
          'categoria.label',
          'categoria',
          'category.label',
          'category',
          'comissao_geral.comissao',
        ])
      )
    ),
  },
  {
    label: 'Valor',
    value: formatMoney(
      firstValue(installment.value, [
        'valor_parcela',
        'amount',
        'value',
      ]) || firstValue(commissionInstallment.value, ['valor', 'amount', 'value'])
    ),
  },
  {
    icon: 'i-lucide-calendar-days',
    label: competenceDate.value ? 'Data de competência' : 'Data da assinatura',
    value: formatDate(competenceDate.value || signatureDate.value),
  },
  {
    icon: 'i-lucide-calendar-days',
    label: dueDate.value ? 'Data de vencimento' : 'Data de criação',
    value: formatDate(dueDate.value || createdStatusAt.value),
  },
  {
    label: 'Nº da parcela',
    value: displayText(
      firstValue(installment.value, [
        'numero_parcela',
        'numero',
        'sequence',
      ]) || firstValue(commissionInstallment.value, ['numero', 'sequence'])
    ),
  },
  {
    label: 'Total de parcelas',
    value: displayText(
      firstValue(installment.value, [
        'total_parcelas',
        'installments_total',
      ]) || asArray(commission.value.parcelas).length
    ),
  },
  {
    icon: 'i-lucide-circle-dot',
    label: 'Status',
    selectLike: true,
    value: statusLabel(currentStatus.value || commissionInstallment.value.status),
  },
  {
    icon: 'i-lucide-landmark',
    label: 'Método de recebimento',
    selectLike: true,
    value: displayText(
      firstValue(source.value, [
        'comissao_geral.forma_pagamento',
        'comissao_geral.tipo_pagamento',
        'payment_method',
      ])
    ),
  },
  {
    icon: 'i-lucide-building-2',
    label: 'Centro de custo',
    selectLike: true,
    value: displayText(firstValue(process.value, ['loja.name', 'store.name'])),
  },
]);
const comparisonState = computed(() =>
  firstValue(source.value, [
    'rascunho.status',
    'draft.status',
    'alteracoes.status',
    'changes.status',
  ])
);
const comparisonStateLabel = computed(() =>
  comparisonState.value
    ? titleCase(statusLabel(comparisonState.value))
    : '—'
);
const hasDraftChanges = computed(() => Boolean(comparisonState.value));
const summaryTitle = computed(() =>
  hasDraftChanges.value ? 'Resumo das alterações' : 'Resumo financeiro'
);
const summaryRows = computed(() => {
  if (hasDraftChanges.value) {
    return [
      {
        current: formatMoney(
          firstValue(installment.value, ['valor_parcela', 'amount', 'value'])
        ),
        detail: '',
        label: 'Valor',
        result: comparisonStateLabel.value,
      },
      {
        current: formatDate(dueDate.value),
        detail: '',
        label: 'Data de vencimento',
        result: comparisonStateLabel.value,
      },
    ];
  }

  return [
    {
      current: grossCommission.value,
      detail: netCommission.value === '—' ? '' : 'Valor líquido',
      label: 'Comissão',
      result: netCommission.value,
    },
    {
      current: rateioTransferred.value,
      detail: rateioAvailable.value === '—' ? '' : 'Disponível para rateio',
      label: 'Rateio',
      result: rateioAvailable.value,
    },
  ];
});
const notes = computed(() =>
  displayText(firstValue(source.value, ['comissao_geral.observacoes', 'notes', 'observacoes']))
);
const hasAvailableAction = computed(() =>
  Boolean(
    (canTransition.value && suggestedStatus.value) ||
      pendingResponsibilities.value.length
  )
);

watch(
  pendingResponsibilities,
  responsibilities => {
    if (
      responsibilities.some(item => item.id === selectedResponsibilityId.value)
    ) {
      return;
    }
    selectedResponsibilityId.value = responsibilities[0]?.id || '';
  },
  { immediate: true }
);

const refresh = () => realData.refresh?.();
const goToDetail = () => emit('navigate', { id: recordId.value, screen: 'detail' });
const applySuggestedStatus = async () => {
  if (!canTransition.value || !suggestedStatus.value || !recordId.value) return;

  notice.value = '';
  try {
    await realData.actions.finance.transition(recordId.value, {
      reason: actionReason.value || undefined,
      target_status: suggestedStatus.value,
    });
    notice.value = 'O status foi atualizado pela API financeira.';
    actionReason.value = '';
  } catch {
    notice.value = '';
  }
};
const confirmPayment = async () => {
  if (!recordId.value || !selectedResponsibilityId.value) return;

  notice.value = '';
  try {
    await realData.actions.finance.confirmPayment(
      recordId.value,
      selectedResponsibilityId.value,
      { confirmed: true }
    );
    notice.value = 'O pagamento foi confirmado pela API financeira.';
  } catch {
    notice.value = '';
  }
};
</script>

<template>
  <main
    data-imudei-visual-id="finance-edit-root"
    class="mx-auto w-full max-w-[1328px] px-7 pb-24 pt-3 text-[#0f2060]"
  >
    <header
      data-imudei-visual-id="finance-edit-header"
      class="relative z-30 flex min-h-[69px] items-start justify-between"
    >
      <div>
        <p class="text-[10px] text-[#4d32b4]">
          Financeiro
          <span class="mx-2 text-[#a5adc1]">/</span>
          {{ recordCode }}
          <span class="mx-2 text-[#a5adc1]">/</span>
          Editar
        </p>
        <div class="mt-1 flex items-center gap-5">
          <h1 class="text-[28px] font-bold leading-none tracking-[-0.035em]">
            Editar lançamento
          </h1>
          <span class="flex items-start gap-2 text-[9px] text-[#50608c]">
            <i class="i-lucide-circle-check mt-0.5 size-5 shrink-0 text-emerald-500" />
            <span>
              <strong class="block font-medium text-[#273a7c]">Dados do lançamento</strong>
              <small class="mt-0.5 block text-[8px] text-[#68749a]">
                {{ loading ? 'Atualizando lançamento…' : 'Sem rascunho retornado pela API' }}
              </small>
            </span>
          </span>
        </div>
      </div>

      <section
        aria-label="Estado de gamificação"
        class="absolute -top-[13px] right-0 flex h-[82px] w-[616px] items-start justify-end gap-[17px] bg-[#fcfbff] pt-[13px]"
      >
        <span
          class="grid size-[60px] shrink-0 place-items-center rounded-full border-2 border-white bg-gradient-to-br from-violet-100 via-white to-emerald-100 text-[12px] font-bold text-[#5b23c8] shadow-[0_0_0_1px_#ddd7ed]"
        >
          <template v-if="gamificationOwner">{{ initials(gamificationOwner) }}</template>
          <i v-else class="i-lucide-medal size-5" />
        </span>
        <span class="w-[210px] pt-1">
          <span class="flex items-center gap-2 text-[9px]">
            <strong>{{ gamificationLevelLabel }}</strong>
            <span class="size-1 rounded-full bg-[#5b23c8]" />
            <small class="font-medium text-[#4d32b4]">
              {{ gamificationStreak ? `Sequência: ${gamificationStreak}` : 'Sequência indisponível' }}
            </small>
          </span>
          <span class="mt-2 flex items-center gap-2">
            <span class="block h-1.5 flex-1 overflow-hidden rounded-full bg-[#eeeaf4]">
              <i
                class="block h-full rounded-full bg-gradient-to-r from-[#5d18d2] to-[#cf22d5]"
                :class="gamificationProgressClass"
              />
            </span>
            <b class="text-[9px] font-semibold text-[#4d32b4]">
              {{ gamificationProgress === null ? '—' : `${Math.round(gamificationProgress)}%` }}
            </b>
          </span>
        </span>
        <span class="h-8 w-px bg-[#e4dfeb]" />
        <span class="flex min-w-[142px] items-center gap-2 text-[#17246a]">
          <i class="i-lucide-star size-5 text-amber-400" />
          <span>
            <strong class="block text-[14px] leading-none">
              {{ gamificationPoints || '—' }}
            </strong>
            <small v-if="!gamificationPoints" class="mt-1 block text-[7px] font-medium text-[#68749a]">
              Pontos indisponíveis
            </small>
          </span>
        </span>
        <span class="grid h-10 min-w-[94px] place-items-center rounded-lg border border-[#d9d2e8] px-3 text-[8px] font-semibold text-[#5b23c8]">
          <i class="i-lucide-gift mr-1 inline size-4" /> Prêmios
        </span>
        <span class="absolute bottom-0 right-0 flex items-center gap-2 text-[8px] text-[#314b9e]">
          <i class="i-lucide-info size-3.5" /> Regras não retornadas pela API
        </span>
      </section>
    </header>

    <section
      data-imudei-visual-id="finance-edit-progress"
      class="mt-6 flex h-[61px] items-center gap-6 rounded-2xl border border-[#e8e4ef] bg-white px-[17px]"
    >
      <strong class="shrink-0 text-[10px]">Como você ganha pontos</strong>
      <span
        v-for="item in pointRules"
        :key="item.id"
        class="flex items-center gap-2 whitespace-nowrap text-[9px] text-[#273a7c]"
      >
        <i :class="item.icon" class="size-4 text-emerald-500" />
        {{ item.label }}
        <b class="text-[#5b23c8]">{{ item.points || '—' }}</b>
      </span>
      <span class="ml-auto flex w-[330px] items-center gap-3 rounded-xl bg-violet-50 px-4 py-3 text-[8px] leading-4 text-[#4d5985]">
        <i class="i-lucide-shield-check size-5 shrink-0 text-[#5b23c8]" />
        Alterações artificiais não pontuam. Regras completas indisponíveis no payload.
      </span>
    </section>

    <section class="mt-5 grid grid-cols-[minmax(0,1fr)_426px] items-start gap-5">
      <div>
          <article
            data-imudei-visual-id="finance-edit-form"
            class="h-[746px] overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white p-[21px] pb-2"
        >
          <section data-imudei-visual-id="finance-edit-steps" class="-mt-5 h-[108px] pt-5">
            <h2 class="text-[11px] font-bold">Tipo de lançamento</h2>
            <div class="mt-3 flex gap-3">
              <span
                class="flex h-[53px] w-[175px] items-center gap-3 rounded-lg border px-4 text-[10px] font-semibold"
                :class="isReceipt ? 'border-emerald-300 text-emerald-600' : 'border-[#e4dfeb] text-[#7b83a1]'"
              >
                <span class="grid size-8 place-items-center rounded-full bg-emerald-50">
                  <i class="i-lucide-arrow-down-to-line size-4" />
                </span>
                Recebimento
              </span>
              <span
                class="flex h-[53px] w-[145px] items-center gap-3 rounded-lg border px-4 text-[10px] font-semibold"
                :class="isPayment ? 'border-orange-300 text-orange-500' : 'border-[#e4dfeb] text-[#7b83a1]'"
              >
                <span class="grid size-8 place-items-center rounded-full bg-orange-50">
                  <i class="i-lucide-arrow-up-from-line size-4" />
                </span>
                Pagamento
              </span>
            </div>
          </section>
          <div class="mb-2 mt-[5px] border-t border-[#eeeaf4]" />

          <div
            v-if="loading || error || !model.detail.value"
            class="mb-3 flex items-start gap-3 rounded-xl border px-3 py-3 text-[8px]"
            :class="error ? 'border-rose-100 bg-rose-50 text-rose-700' : 'border-violet-100 bg-violet-50 text-[#4d5985]'"
          >
            <i
              :class="error ? 'i-lucide-circle-alert text-rose-500' : 'i-lucide-file-search text-[#5b23c8]'"
              class="mt-0.5 size-4 shrink-0"
            />
            <span class="min-w-0 flex-1">
              <strong class="block text-[9px]">
                {{ loading ? 'Atualizando dados do lançamento' : error ? 'Lançamento indisponível' : 'Lançamento não disponível' }}
              </strong>
              <small class="mt-1 block leading-4">
                {{ loading ? 'A estrutura de edição permanece disponível enquanto a API responde.' : error || 'A API não retornou um lançamento financeiro para este identificador.' }}
              </small>
            </span>
            <button
              v-if="error"
              class="h-7 shrink-0 rounded-lg border border-rose-200 bg-white px-3 text-[8px] font-semibold text-rose-700"
              type="button"
              @click="refresh"
            >
              Tentar novamente
            </button>
          </div>

          <label class="block text-[9px] font-semibold leading-4">
            Vincular à venda
            <small class="ml-1 text-[8px] font-medium leading-4 text-[#7580a1]">(obrigatório)</small>
            <span class="mt-2 flex h-[61px] items-center gap-3">
              <span class="flex h-full min-w-0 flex-1 items-center gap-3 overflow-hidden rounded-lg border border-[#ded9e9] px-3">
                <span class="grid size-10 place-items-center rounded-lg bg-[#f1edff] text-[#5b23c8]">
                  <i class="i-lucide-building-2 size-5" />
                </span>
                <span class="min-w-0 flex-1">
                  <strong class="block truncate text-[10px] leading-4">{{ recordCode }} · {{ recordTitle }}</strong>
                  <small class="mt-1 block truncate text-[8px] leading-3 text-[#68749a]">{{ recordContext }}</small>
                </span>
                <i class="i-lucide-chevron-down size-4 text-[#5b23c8]" />
              </span>
              <span class="grid size-[61px] shrink-0 place-items-center rounded-lg border border-[#ded9e9] text-[#5b23c8]">
                <i class="i-lucide-external-link size-4" />
              </span>
            </span>
          </label>

          <section class="mt-3 leading-4">
            <div class="flex items-center justify-between">
              <span class="text-[9px] font-semibold leading-4">
                {{ payers.length === 1 ? 'Beneficiário' : 'Beneficiários' }}
                <small class="ml-1 text-[8px] font-medium leading-4 text-[#7580a1]">(obrigatório)</small>
              </span>
            </div>
            <div v-if="payers.length" class="mt-2 flex flex-wrap gap-2">
              <span
                v-for="payer in payers"
                :key="payer.id"
                class="flex h-12 min-w-[170px] items-center gap-2 rounded-lg border border-[#ded9e9] px-3"
              >
                <i class="grid size-7 place-items-center rounded-full bg-[#4315a8] text-[8px] not-italic text-white">
                  {{ initials(payer.name) }}
                </i>
                <span class="min-w-0">
                  <b class="block truncate text-[9px] leading-3">{{ payer.name }}</b>
                  <small v-if="payer.role" class="block truncate text-[8px] leading-3 text-[#68749a]">{{ payer.role }}</small>
                </span>
              </span>
            </div>
            <p v-else class="mt-2 flex h-12 items-center gap-2 rounded-lg bg-[#faf9fd] px-3 text-[9px] text-[#68749a]">
              <i class="i-lucide-users size-4 text-[#7e5bd7]" />
              —
            </p>
          </section>

          <label class="mt-3 block text-[9px] font-semibold leading-4">
            Descrição
            <small class="ml-1 text-[8px] font-medium leading-4 text-[#7580a1]">(obrigatório)</small>
            <span class="mt-2 flex h-10 items-center rounded-lg border border-[#ded9e9] px-3 text-[10px] font-normal leading-4 text-[#273a7c]">
              {{ displayText(firstValue(source, ['description', 'descricao', 'comissao_geral.descricao'])) }}
            </span>
          </label>

          <div class="mt-3 grid grid-cols-3 gap-x-3 gap-y-3">
            <label v-for="field in fields" :key="field.label" class="block text-[9px] font-semibold leading-4">
              {{ field.label }}
              <small class="ml-1 text-[8px] font-medium leading-4 text-[#7580a1]">(obrigatório)</small>
              <span class="mt-2 flex h-10 items-center gap-2 rounded-lg border border-[#ded9e9] px-3 text-[9px] font-normal leading-4 text-[#273a7c]">
                <i
                  v-if="field.icon"
                  :class="field.icon"
                  class="size-3.5 text-[#595284]"
                />
                <span class="truncate">{{ field.value }}</span>
                <i
                  v-if="field.selectLike"
                  class="i-lucide-chevron-down ml-auto size-3 text-[#68749a]"
                />
              </span>
            </label>
          </div>

          <label class="mt-3 block text-[9px] font-semibold leading-4">
            Observações
            <span class="mt-2 flex min-h-[58px] items-start rounded-lg border border-[#ded9e9] px-3 py-3 text-[9px] font-normal leading-4 text-[#273a7c]">
              {{ notes }}
            </span>
          </label>

          <p class="mt-[11px] flex h-7 items-center gap-2 rounded-lg border border-[#ebe7f1] bg-[#faf9fd] px-3 py-0 text-[8px] leading-4 text-[#68749a]">
            <i class="i-lucide-sparkles size-4 text-[#5b23c8]" />
            Editar não gera pontos. {{ actionAvailability }}
          </p>

          <section v-if="hasAvailableAction" class="mt-3 border-t border-[#eeeaf4] pt-3">
            <div class="flex items-center justify-between">
              <span>
                <h2 class="text-[11px] font-bold">Ações permitidas</h2>
                <small class="mt-1 block text-[8px] text-[#68749a]">
                  Controles disponibilizados pelo backend para este lançamento.
                </small>
              </span>
              <span class="rounded-full bg-violet-50 px-2 py-1 text-[8px] font-semibold text-[#5b23c8]">API real</span>
            </div>

            <div class="mt-3 space-y-3">
              <label
                v-if="canTransition && suggestedStatus"
                class="block text-[8px] font-semibold"
              >
                Motivo da transição
                <textarea
                  v-model="actionReason"
                  class="mt-1.5 min-h-[52px] w-full rounded-lg border border-[#ded9e9] px-3 py-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
                  placeholder="Opcional, enviado à API financeira"
                />
              </label>

              <div
                v-if="canTransition && suggestedStatus"
                class="flex items-center justify-between rounded-xl border border-violet-100 bg-violet-50 px-3 py-3"
              >
                <span>
                  <strong class="block text-[9px]">Aplicar sugestão do backend</strong>
                  <small class="mt-1 block text-[8px] text-[#68749a]">
                    Próximo status: {{ titleCase(suggestedStatus) }}
                  </small>
                </span>
                <button
                  class="h-8 rounded-lg border border-[#cbbcff] bg-white px-3 text-[8px] font-semibold text-[#5421c7] disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  :disabled="saving"
                  @click="applySuggestedStatus"
                >
                  {{ saving ? 'Processando…' : 'Atualizar status' }}
                </button>
              </div>

              <div
                v-if="pendingResponsibilities.length"
                class="flex items-end gap-3 rounded-xl border border-[#e8e4ef] bg-[#fcfbff] p-3"
              >
                <label class="min-w-0 flex-1 text-[8px] font-semibold">
                  Responsabilidade de pagamento
                  <select
                    v-model="selectedResponsibilityId"
                    class="mt-1.5 h-8 w-full rounded-lg border border-[#ded9e9] bg-white px-2 text-[9px] font-normal outline-none focus:border-[#805ce1]"
                  >
                    <option
                      v-for="responsibility in pendingResponsibilities"
                      :key="responsibility.id"
                      :value="responsibility.id"
                    >
                      {{ responsibility.name }} · {{ responsibility.value }}
                    </option>
                  </select>
                </label>
                <button
                  class="h-8 rounded-lg bg-gradient-to-r from-[#5d18d2] to-[#b824dd] px-3 text-[8px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  :disabled="saving || !selectedResponsibilityId"
                  @click="confirmPayment"
                >
                  Confirmar pagamento
                </button>
              </div>
            </div>
          </section>

          <p
            v-if="notice"
            class="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-[8px] text-emerald-700"
          >
            {{ notice }}
          </p>
          <p
            v-if="model.localError.value"
            class="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-[8px] text-rose-600"
          >
            {{ model.localError.value }}
          </p>
        </article>
      </div>

      <aside data-imudei-visual-id="finance-edit-sidebar" class="mt-1 space-y-[11px]">
        <article class="min-h-[224px] overflow-hidden rounded-2xl border border-[#e8e4ef] border-t-2 border-t-[#6d22df] bg-white p-[17px]">
          <header class="flex items-center justify-between">
            <h2 class="text-[11px] font-bold">{{ summaryTitle }}</h2>
            <span class="flex items-center gap-2 text-[8px] text-[#5b23c8]">
              <span>{{ hasDraftChanges ? 'Ver comparação' : 'Comparação indisponível' }}</span>
              <i class="i-lucide-eye size-3.5" />
            </span>
          </header>
          <div class="mt-4 grid grid-cols-3 border-b border-[#eeeaf4] pb-3 text-[8px] text-[#637098]">
            <span>Campo</span>
            <span>Valor atual</span>
            <span>{{ hasDraftChanges ? 'Novo valor' : 'Operação' }}</span>
          </div>
          <div
            v-for="row in summaryRows"
            :key="row.label"
            class="grid grid-cols-3 items-center border-b border-[#eeeaf4] py-3.5 text-[9px] last:border-0"
          >
            <b>{{ row.label }}</b>
            <span class="min-w-0 text-[#465789]">
              <span class="block truncate">{{ row.current }}</span>
            </span>
            <span class="min-w-0 text-emerald-600">
              <span class="flex items-center gap-1 truncate font-semibold">
                <i v-if="hasDraftChanges" class="i-lucide-arrow-up-right size-3" />
                {{ row.result }}
              </span>
              <small v-if="row.detail" class="block truncate text-[7px] text-[#465789]">{{ row.detail }}</small>
            </span>
          </div>
          <p v-if="hasDraftChanges" class="mt-3 text-[8px] text-[#68749a]">
            Comparação retornada pelo backend.
          </p>
        </article>

        <article class="min-h-[190px] rounded-2xl border border-[#e8e4ef] bg-white p-[17px]">
          <header class="flex items-center justify-between">
            <h2 class="text-[11px] font-bold">Trilha de auditoria</h2>
            <span class="text-[8px] text-[#5b23c8]">{{ events.length }} eventos</span>
          </header>
          <div v-if="auditItems.length">
            <div v-for="item in auditItems" :key="item.id" class="mt-4 flex gap-3">
              <span class="grid size-8 shrink-0 place-items-center rounded-full bg-violet-100 text-[8px] font-bold text-[#5b23c8]">
                {{ initials(item.actor) }}
              </span>
              <span class="min-w-0">
                <strong class="block truncate text-[9px]">{{ item.actor }}</strong>
                <em class="mt-1 inline-block rounded bg-violet-100 px-2 py-1 text-[7px] not-italic text-[#5b23c8]">Auditoria</em>
                <small class="mt-1 block text-[8px] text-[#4f5e88]">{{ item.label }}</small>
                <small v-if="item.reason" class="mt-1 block text-[7px] text-[#68749a]">{{ item.reason }}</small>
                <small class="block text-[7px] text-[#8790aa]">{{ item.date }}</small>
              </span>
            </div>
          </div>
          <div v-else class="mt-4 space-y-3">
            <div class="flex gap-3">
              <span class="grid size-8 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5b23c8]">
                <i class="i-lucide-history size-4" />
              </span>
              <span>
                <strong class="block text-[9px]">Auditoria indisponível</strong>
                <small class="mt-1 block text-[8px] text-[#4f5e88]">
                  A API não retornou eventos para este lançamento.
                </small>
              </span>
            </div>
            <div class="flex gap-3">
              <span class="grid size-8 shrink-0 place-items-center rounded-full bg-[#f1edff] text-[#7e5bd7]">
                <i class="i-lucide-clock-3 size-4" />
              </span>
              <span>
                <strong class="block text-[9px]">Sem histórico complementar</strong>
                <small class="mt-1 block text-[7px] text-[#8790aa]">
                  Nenhuma data de auditoria foi disponibilizada.
                </small>
              </span>
            </div>
          </div>
        </article>

        <article class="min-h-[195px] rounded-2xl border border-[#e8e4ef] bg-white p-[17px]">
          <header class="flex items-center justify-between">
            <h2 class="text-[11px] font-bold">Atividade recente</h2>
            <span class="text-[8px] text-[#5b23c8]">Fonte: API</span>
          </header>
          <div v-if="activityItems.length">
            <div v-for="item in activityItems" :key="item.id" class="mt-3 flex items-center gap-2">
              <span class="grid size-7 shrink-0 place-items-center rounded-full bg-[#f1edff] text-[7px] font-bold text-[#5b23c8]">
                {{ initials(item.actor) }}
              </span>
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[8px]">{{ item.label }}</strong>
                <small class="block truncate text-[7px] text-[#8790aa]">{{ item.actor }} · {{ item.date }}</small>
              </span>
              <b v-if="item.value" class="text-[8px] font-semibold text-[#5b23c8]">{{ item.value }}</b>
            </div>
          </div>
          <div v-else class="mt-4 space-y-3">
            <div class="flex items-center gap-2">
              <span class="grid size-7 shrink-0 place-items-center rounded-full bg-[#f1edff] text-[#5b23c8]">
                <i class="i-lucide-clock-3 size-3.5" />
              </span>
              <span class="min-w-0 flex-1">
                <strong class="block text-[8px]">Atividade indisponível</strong>
                <small class="block text-[7px] text-[#8790aa]">Sem eventos no payload atual.</small>
              </span>
            </div>
            <div class="flex items-center gap-2">
              <span class="grid size-7 shrink-0 place-items-center rounded-full bg-[#f1edff] text-[#5b23c8]">
                <i class="i-lucide-star size-3.5" />
              </span>
              <span class="min-w-0 flex-1">
                <strong class="block text-[8px]">Pontuação indisponível</strong>
                <small class="block text-[7px] text-[#8790aa]">Nenhum ponto foi retornado pela API.</small>
              </span>
            </div>
            <div class="flex items-center gap-2">
              <span class="grid size-7 shrink-0 place-items-center rounded-full bg-[#f1edff] text-[#5b23c8]">
                <i class="i-lucide-list-checks size-3.5" />
              </span>
              <span class="min-w-0 flex-1">
                <strong class="block text-[8px]">Feed não disponível</strong>
                <small class="block text-[7px] text-[#8790aa]">Atualize quando a API publicar atividades.</small>
              </span>
            </div>
          </div>
        </article>

        <article class="min-h-[98px] overflow-hidden rounded-2xl border border-[#e8e4ef] border-t-2 border-t-[#6d22df] bg-white p-[17px]">
          <header class="flex justify-between">
            <h2 class="text-[11px] font-bold">Missão diária</h2>
            <span class="text-[8px] text-[#7580a1]">Indisponível</span>
          </header>
          <div class="mt-4 flex items-center gap-3 text-[#68749a]">
            <span class="grid size-9 place-items-center rounded-full bg-violet-50 text-[#5b23c8]">
              <i class="i-lucide-target size-5" />
            </span>
            <span class="flex-1">
              <strong class="block text-[9px] font-medium text-[#273a7c]">Metas não disponíveis</strong>
              <small class="mt-1 block text-[8px]">
                Este módulo não expõe missões no backend.
              </small>
              <span class="mt-2 block h-1.5 overflow-hidden rounded-full bg-[#e9e7ef]">
                <i class="block h-full w-0 rounded-full bg-[#5b23c8]" />
              </span>
            </span>
            <b class="text-[8px] text-[#5b23c8]">—</b>
          </div>
        </article>
      </aside>
    </section>

    <footer
      data-imudei-visual-id="finance-edit-footer"
      class="fixed bottom-[18px] left-[242px] right-[27px] z-50 flex h-11 items-center bg-[#fefefe] px-0"
    >
      <button
        class="h-11 w-[96px] rounded-lg border border-[#d9d2e8] bg-white text-[10px] font-medium"
        type="button"
        @click="goToDetail"
      >
        Cancelar
      </button>
      <div class="ml-auto flex items-center gap-4">
        <button
          class="mr-[63px] flex h-10 w-[142px] translate-y-1 items-center justify-center gap-2 rounded-lg border border-[#d9d2e8] bg-white text-[10px] font-medium text-[#5b23c8] disabled:cursor-not-allowed disabled:opacity-100"
          type="button"
          disabled
        >
          <i class="i-lucide-refresh-cw size-3.5" />
          Comparar alterações
        </button>
        <button
          class="h-10 w-[128px] translate-y-1 rounded-lg border border-[#d9d2e8] bg-white text-[10px] font-semibold text-[#5b23c8] disabled:cursor-not-allowed disabled:opacity-100"
          type="button"
          disabled
        >
          Salvar rascunho
        </button>
        <button
          class="h-10 w-[224px] translate-y-1 rounded-lg bg-gradient-to-r from-[#5d18d2] to-[#b824dd] text-[10px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-100"
          type="button"
          disabled
        >
          Salvar alterações
        </button>
      </div>
    </footer>
  </main>
</template>
